import React from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import Skeleton from 'react-loading-skeleton';
import HBClickable from '../HBClickable/HBClickable';
import type { DateObj } from 'dayzed';
import HBText from '../HBText/HBText';
import HBStatus from '../HBStatus/HBStatus';
import { applyOpacity } from '../../utils/color.utils';
import spacing from '../../styles/spacing';
import { hoverTransition, transition } from '../../styles/animations';
import type { IDayPickerBaseProps } from './HBDayPicker.types';

interface Props extends IDayPickerBaseProps {
    dayzedDateObject?: '' | DateObj;
    dayzedDateProps?: any;
    className?: string;
    isLoading?: boolean;
}

const DayButton = ({
    dayzedDateObject,
    dayzedDateProps,
    backgroundColor,
    baseTextColor,
    availabilityStatusTheme,
    selectedTextColor,
    isDateSelectable,
    isDisabled,
    className,
    isLoading
}: Props) => {
    if (!dayzedDateObject) {
        return (
            <Container isDisabled onClick={null} testId="day-button-skeleton" />
        );
    }

    const { date, today, selected, selectable } = dayzedDateObject;
    const canSelectDate = isDateSelectable(date) && selectable;
    const _isDisabled = isDisabled || !canSelectDate;

    // calc colors
    let dayBackgroundColor: string = 'transparent';
    let dayTextColor = baseTextColor;

    if (isLoading) {
        dayBackgroundColor = 'transparent';
    } else if (!canSelectDate) {
        dayTextColor = applyOpacity(baseTextColor, 30);
    } else if (selected) {
        dayBackgroundColor = backgroundColor;
        dayTextColor = selectedTextColor;
    } else if (today) {
        dayBackgroundColor = applyOpacity(backgroundColor, 10);
    }

    const hoverBackgroundColor = canSelectDate
        ? applyOpacity(backgroundColor, 20)
        : '';

    const hoverTextColor = canSelectDate ? backgroundColor : '';

    // end calc colors

    const testId = `DayButton-${new Date(date).toDateString()}`;

    const showAvailabilityStatus =
        availabilityStatusTheme && canSelectDate && !isLoading;

    return (
        <Container
            className={className}
            isDisabled={_isDisabled}
            $isLoading={isLoading}
            testId={testId}
            $backgroundColor={dayBackgroundColor}
            $selected={selected}
            $hoverBackgroundColor={hoverBackgroundColor}
            $hoverTextColor={hoverTextColor}
            {...dayzedDateProps}
        >
            {isLoading ? (
                <Skeleton />
            ) : (
                <DayCellWrapper>
                    <DayText type="body14" textColor={dayTextColor}>
                        {date.getDate()}
                    </DayText>
                    {showAvailabilityStatus && (
                        <DayPickerAvailabilityStatus
                            type="dot"
                            theme={availabilityStatusTheme}
                            size="sm"
                            testIdName={`hb-day-picker-day-availability-${date.getTime()}`}
                        />
                    )}
                </DayCellWrapper>
            )}
        </Container>
    );
};

export default DayButton;

// --- Styles ---

const DayText = styled(HBText)<{ textColor?: string }>`
    color: ${({ textColor }) => textColor || vars.colors.text.primary};
    ${transition('color')};
`;

const DayCellWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
`;

const DayPickerAvailabilityStatus = styled(HBStatus)`
    margin-top: ${spacing[2]};
    line-height: 0;

    && {
        height: ${spacing[6]};
    }
`;

const Container = styled(HBClickable)<{
    $backgroundColor?: string;
    $hoverBackgroundColor?: string;
    $hoverTextColor?: string;
    $selected?: boolean;
    $isLoading?: boolean;
}>`
    display: flex;
    width: 36px;
    height: 36px;
    justify-content: center;
    align-items: center;
    border-radius: ${vars.radius.full};
    background-color: ${({ $backgroundColor }) =>
        $backgroundColor || 'transparent'};
    ${({
        $selected,
        isDisabled,
        $hoverTextColor,
        $backgroundColor,
        $hoverBackgroundColor
    }) =>
        !$selected &&
        !isDisabled &&
        css`
            ${hoverTransition(
                'background-color',
                $backgroundColor,
                $hoverBackgroundColor
            )}
            &:hover,
            &:focus-visible {
                ${DayText} {
                    color: ${$hoverTextColor || 'inherit'};
                }
            }
        `}
    // loading skeleton styles
    ${({ $isLoading }) =>
        $isLoading &&
        css`
            span {
                display: block;
                border-radius: ${vars.radius.full};
                width: 100%;
                height: 100%;
            }
        `}
`;
